import { useQuery } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { api } from './client';
import type { Address } from '../../features/account/address';

export interface Account {
  accountId: string;
  name: string;
  status: string;
  swftEnabled?: boolean;
  doceEnabled?: boolean;
  /** Workflows this account is authorized for, beyond the two with their own flags. */
  authorizedCaptureTypes?: string[];
  doceUserId?: string | null;
  doceAttorneyAcct?: boolean;
  storageActive?: boolean;
  stripeCustomerId?: string | null;
  rdOptOut?: boolean;
  physicalAddress?: Address;
  billingAddress?: Address;
}

/** The caller's account item (includes SWFT/DOCE provisioning flags). */
export function useAccount() {
  return useQuery({ queryKey: ['account'], queryFn: () => api.get<Account>('/account') });
}

/** Raw JWT claims (custom:accountId, custom:role, cognito:groups). */
export function useClaims() {
  return useQuery({
    queryKey: ['claims'],
    queryFn: async () => {
      const s = await fetchAuthSession();
      return (s.tokens?.idToken?.payload ?? {}) as Record<string, unknown>;
    },
  });
}

/** True when the signed-in user is Lakota platform staff (Cognito SiteAdmin group). */
export function useIsSiteAdmin(): boolean {
  const claims = useClaims();
  const groups = claims.data?.['cognito:groups'];
  const list = Array.isArray(groups) ? groups.map(String) : typeof groups === 'string' ? [groups] : [];
  return list.includes('SiteAdmin');
}

/** The caller's per-account role (custom:role claim). */
export function useRole(): string | undefined {
  const claims = useClaims();
  const r = claims.data?.['custom:role'];
  return typeof r === 'string' ? r : undefined;
}

// Client mirror of services/lib/auth/permissions.ts MATRIX (nav visibility only; server always enforces).
const CAPS: Record<string, readonly string[]> = {
  AccountAdmin: ['Capture', 'ViewOwnTransactions', 'ViewAccountTransactions', 'ManageDevices', 'AllocateLicenses', 'ManageUsers', 'Billing', 'ManageLocations', 'DoceConfig'],
  AccountManager: ['Capture', 'ViewOwnTransactions', 'ViewAccountTransactions', 'ManageDevices', 'ManageLocations'],
  Operator: ['Capture', 'ViewOwnTransactions'],
  Viewer: ['ViewOwnTransactions', 'ViewAccountTransactions'],
};

/**
 * Returns a `can(capability)` predicate for gating nav/UI to the caller's role. SiteAdmin gets all.
 * Returns a function (not a hook) so it can be called inside filters/conditionals.
 */
export function useCan(): (capability: string) => boolean {
  const isSiteAdmin = useIsSiteAdmin();
  const role = useRole();
  return (capability: string) => {
    if (isSiteAdmin) return true;
    return (role ? CAPS[role] ?? [] : []).includes(capability);
  };
}
